import { ArrowUpCircle, ArrowDownCircle, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { InvestmentTransaction } from '@/types/investments';

interface InvestmentTransactionListProps {
  transactions: InvestmentTransaction[];
  isLoading: boolean;
  currency: string;
  onTransactionClick?: (transaction: InvestmentTransaction) => void;
}

const ITEMS_PER_PAGE = 50;

export function InvestmentTransactionList({
  transactions,
  isLoading,
  currency,
  onTransactionClick,
}: InvestmentTransactionListProps) {
  const [currentPage, setCurrentPage] = useState(1);

  const formatCurrency = (amount: number, curr: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(transactions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedTransactions = transactions.slice(startIndex, endIndex);
  const showPagination = transactions.length > ITEMS_PER_PAGE;

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <Skeleton className="h-6 w-20" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="flex flex-col items-center gap-2">
            <div className="rounded-full bg-muted p-3">
              <ArrowUpCircle className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-lg">No transactions found</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              No transactions have been recorded for this investment account yet. Add your first deposit or withdrawal to get started.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <Card>
          <CardContent className="p-0">
            <table className="w-full" role="table" aria-label="Investment transaction list">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-4 font-medium text-sm text-muted-foreground">Date</th>
                  <th className="text-left p-4 font-medium text-sm text-muted-foreground">Type</th>
                  <th className="text-left p-4 font-medium text-sm text-muted-foreground">Amount</th>
                  <th className="text-left p-4 font-medium text-sm text-muted-foreground">Fee</th>
                  <th className="text-left p-4 font-medium text-sm text-muted-foreground">Notes</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTransactions.map((transaction) => {
                  const isDeposit = transaction.transaction_subtype === 'investment_deposit';
                  const isInterest = transaction.transaction_subtype === 'investment_interest';
                  const isIncome = isDeposit || isInterest;
                  const amountColor = isIncome ? 'text-green-600' : 'text-red-600';
                  const transactionCurrency = transaction.currency || currency;

                  return (
                    <tr
                      key={transaction.id}
                      className={cn(
                        'border-b last:border-b-0 transition-colors',
                        onTransactionClick && 'cursor-pointer hover:bg-muted/50'
                      )}
                      onClick={() => onTransactionClick?.(transaction)}
                      role="row"
                    >
                      <td className="p-4 text-sm">{formatDate(transaction.transaction_date)}</td>
                      <td className="p-4">
                        <Badge variant={isIncome ? 'default' : 'secondary'}>
                          {isDeposit ? 'Deposit' : isInterest ? 'Interest' : 'Withdrawal'}
                        </Badge>
                      </td>
                      <td className={cn('p-4 font-semibold', amountColor)}>
                        {isIncome ? '+' : '-'}
                        {formatCurrency(transaction.amount, transactionCurrency)}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {transaction.fee > 0 ? formatCurrency(transaction.fee, transactionCurrency) : '-'}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground truncate max-w-xs">
                        {transaction.notes || transaction.description || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3" role="list" aria-label="Investment transaction list">
        {paginatedTransactions.map((transaction) => {
          const isDeposit = transaction.transaction_subtype === 'investment_deposit';
          const isInterest = transaction.transaction_subtype === 'investment_interest';
          const isIncome = isDeposit || isInterest;
          const Icon = isDeposit ? ArrowUpCircle : isInterest ? TrendingUp : ArrowDownCircle;
          const iconColor = isIncome ? 'text-green-600' : 'text-red-600';
          const amountColor = isIncome ? 'text-green-600' : 'text-red-600';
          const transactionCurrency = transaction.currency || currency;
          const typeLabel = isDeposit ? 'Deposit' : isInterest ? 'Interest income' : 'Withdrawal';

          return (
            <Card
              key={transaction.id}
              className={cn(
                'transition-colors',
                onTransactionClick && 'cursor-pointer hover:bg-accent focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2'
              )}
              role="article"
              aria-label={`${typeLabel} transaction: ${formatCurrency(transaction.amount, transactionCurrency)} on ${formatDate(transaction.transaction_date)}`}
            >
              <CardContent className="p-4">
                <button
                  onClick={() => onTransactionClick?.(transaction)}
                  className="w-full text-left focus:outline-none min-h-[44px]"
                  disabled={!onTransactionClick}
                  aria-label={`View details for ${typeLabel.toLowerCase()} of ${formatCurrency(transaction.amount, transactionCurrency)} on ${formatDate(transaction.transaction_date)}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {/* Icon */}
                      <div className={cn('flex-shrink-0 mt-1', iconColor)} aria-hidden="true">
                        <Icon className="h-8 w-8" />
                      </div>

                      {/* Transaction Details */}
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant={isIncome ? 'default' : 'secondary'}>
                            {isDeposit ? 'Deposit' : isInterest ? 'Interest' : 'Withdrawal'}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(transaction.transaction_date)}
                        </p>
                        {(transaction.notes || transaction.description) && (
                          <p className="text-sm text-muted-foreground truncate">
                            {transaction.notes || transaction.description}
                          </p>
                        )}
                        {transaction.fee > 0 && (
                          <p className="text-xs text-muted-foreground">
                            Fee: {formatCurrency(transaction.fee, transactionCurrency)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="flex-shrink-0">
                      <span className={cn('font-semibold text-lg', amountColor)}>
                        <span className="sr-only">{typeLabel}: </span>
                        {isIncome ? '+' : '-'}
                        {formatCurrency(transaction.amount, transactionCurrency)}
                      </span>
                    </div>
                  </div>
                </button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pagination Controls */}
      {showPagination && (
        <div className="flex items-center justify-between pt-4">
          <p className="text-sm text-muted-foreground">
            Showing {startIndex + 1}-{Math.min(endIndex, transactions.length)} of {transactions.length} transactions
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              aria-label="Previous page"
              className="min-h-[44px] min-w-[44px]"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              aria-label="Next page"
              className="min-h-[44px] min-w-[44px]"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
